import React from "react";
import { Sparkle } from "@phosphor-icons/react";
import RichReflectionEditor from "./RichReflectionEditor";
import RichReflectionRenderer, { isRichReflection } from "./RichReflectionRenderer";

/**
 * CurrentReflectionBlock — the governed Current Reflection section of
 * the Reflection Canvas article. Extracted verbatim from
 * `ReflectionCanvasArticle.jsx` during the final Embrace hardening pass.
 *
 * Renders three surfaces in order:
 *   1. Metadata column (§ Current Reflection · version · author · date)
 *   2. Body — read: Playfair drop-cap paragraph or empty-state italic
 *      copy; edit: Merriweather Textarea with AI-drafted banner when the
 *      draft came from YadidAi's Digest, followed by Review → Snapshot
 *      confirmation OR Cancel.
 *   3. Actions row — "Update current reflection" pill, an optional
 *      "Summarize what I said →" digest link (shown only when the
 *      current viewer has guided Working Journal entries here), and
 *      the "§ History · N prior versions" eyebrow.
 *
 * Provenance chip — appears BENEATH the read-mode paragraph when the
 * canonical `where_we_are_now_generated_by` is `yadidai`. Visually
 * secondary (monospace eyebrow, brass sparkle, no filled background).
 * Preserves source links: any persisted `source_reflection_ids` that
 * still live in the Working Journal render as a subtle "· N sources"
 * button that scrolls to the first still-linked entry.
 *
 * Behaviour, endpoints, styling, and every `data-testid` are preserved
 * unchanged from the pre-extraction inline version.
 */
export default function CurrentReflectionBlock({
  columnMode = false,
  testidBase,
  currentReflection,
  currentUpdatedAt,
  currentUpdatedBy,
  historyCount,
  history = [],
  currentGeneratedBy,
  currentSourceIds,
  reflections,
  editingCurrent,
  currentDraft,
  setCurrentDraft,
  confirmSnapshot,
  setConfirmSnapshot,
  savingCurrent,
  digestGeneratedBy,
  digestSources,
  digestLoading,
  hasMyGuidedEntries,
  onOpenEditor,
  onCancelEdit,
  onRunDigest,
  onCommit,
}) {
  // Provenance chip lineage — only surfaces after explicit advisor
  // approval (persisted `generated_by === 'yadidai'`) and only in read
  // mode. Never shown while editing to avoid mixing lineage of a prior
  // canonical body with a new-in-progress draft.
  const showProvenance =
    !editingCurrent && !!currentReflection && currentGeneratedBy === "yadidai";
  // Preserve source links where available — intersect persisted source
  // ids with entries still present in the Working Journal stream. Ids
  // that have been deleted or filtered out (visibility) simply drop.
  const linkedSources = (currentSourceIds || []).filter(
    (id) => (reflections || []).some((r) => r.id === id)
  );

  const scrollToFirstSource = () => {
    const first = linkedSources[0];
    if (!first) return;
    const el = document.querySelector(
      `[data-testid="${testidBase}-journal-entry-${first}"]`
    );
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Column mode: renders as an article column with a Navy underline
  // eyebrow ("§ CURRENT REFLECTION · GOVERNED   Present considered
  // judgment"). Legacy mode kept for any callers still using the
  // 180px-metadata + body grid.
  // `min-w-0` is CRITICAL here — every ancestor from the grid track
  // down must allow shrinking so `overflow-wrap: anywhere` on the
  // reflection body can actually take effect. A grid/flex item's
  // default `min-width: auto` (== content size) would otherwise let a
  // 500-char unbroken token push this column wider than its track.
  //
  // Feb 2026 · Iter 54 — In columnMode this column becomes an
  // independently scrollable reading pane inside the bounded canvas
  // grid: `md:h-full md:min-h-0 md:overflow-y-auto`. On mobile the
  // column keeps its natural stacked flow (no forced height). Vertical
  // padding stays the same either way.
  const outerCls = columnMode
    ? "px-6 md:px-10 py-8 md:py-10 min-w-0 md:h-full md:min-h-0 md:overflow-y-auto md:overscroll-contain"
    : "border-t border-border px-5 md:px-8 py-6 md:py-7 grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 min-w-0";

  return (
    <div className={outerCls} data-testid={`${testidBase}-current`}>
      {columnMode && (
        <div className="border-b border-primary pb-3 mb-6 flex items-baseline justify-between gap-4 flex-wrap">
          <div className="text-[0.6875rem] font-mono uppercase tracking-[0.28em] text-primary flex items-center gap-2">
            <span>§ Current Reflection</span>
            <span className="text-border">·</span>
            <span>Governed</span>
          </div>
          <div className="text-[0.8125rem] italic text-muted-foreground" style={{ fontFamily: "'Merriweather', serif" }}>
            Present considered judgment
          </div>
        </div>
      )}
      {!columnMode && (
      <div>
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-primary">§ Current Reflection</div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1.5 leading-relaxed">
          Version {historyCount + 1}<br />
          {currentUpdatedBy && <>By · {currentUpdatedBy}<br /></>}
          {currentUpdatedAt && <>Updated · {new Date(currentUpdatedAt).toLocaleDateString()}</>}
        </div>
        <div className="text-xs italic text-muted-foreground mt-3 leading-relaxed" style={{ fontFamily: "'Merriweather', serif" }}>
          The advisor's present considered judgment. Updating snapshots the prior version — nothing is lost.
        </div>
      </div>
      )}
      <div className="min-w-0">
        {!editingCurrent && currentReflection && (
          <RichReflectionRenderer
            body={currentReflection}
            columnMode={columnMode}
            dropCap
            testid={`${testidBase}-current-body`}
          />
        )}
        {!editingCurrent && !currentReflection && (
          <p className="text-sm italic text-muted-foreground" style={{ fontFamily: "'Merriweather', serif" }}>
            No current reflection yet. Promote a journal entry below, or start one directly.
          </p>
        )}

        {/* Provenance chip — subtle, secondary. Present only when the
            canonical body was AI-drafted and then explicitly approved. */}
        {showProvenance && (
          <div
            className="mt-3 flex flex-wrap items-center gap-1.5 text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground"
            data-testid={`${testidBase}-current-provenance-chip`}
            title="This reflection was drafted by YadidAi and explicitly approved by the advisor before promotion."
          >
            <Sparkle size={10} weight="fill" className="text-brass/70" aria-hidden />
            <span>AI-drafted · Advisor-approved</span>
            {linkedSources.length > 0 && (
              <>
                <span className="text-border">·</span>
                <button
                  type="button"
                  onClick={scrollToFirstSource}
                  data-testid={`${testidBase}-current-provenance-sources`}
                  aria-label="Jump to source entries in the Working Journal"
                  className="hover:text-brass border-b border-transparent hover:border-brass pb-0.5"
                >
                  {linkedSources.length} source{linkedSources.length === 1 ? "" : "s"}
                </button>
              </>
            )}
          </div>
        )}

        {editingCurrent && (
          <div>
            {digestGeneratedBy === "yadidai" && (
              <div
                className="mb-3 border-l-2 border-brass pl-3 py-2 bg-brass/5 flex items-start gap-2"
                data-testid={`${testidBase}-current-ai-banner`}
                role="note"
              >
                <Sparkle size={14} weight="fill" className="text-brass shrink-0 mt-0.5" aria-hidden />
                <div className="text-xs leading-relaxed" style={{ fontFamily: "'Merriweather', serif" }}>
                  <span className="font-mono uppercase tracking-widest text-[0.6875rem] text-brass">AI-drafted · review before promoting</span>
                  <div className="text-muted-foreground italic mt-0.5">
                    YadidAi synthesised this from {digestSources.length} of your Working Journal {digestSources.length === 1 ? "entry" : "entries"}. Nothing has been promoted yet — edit as you wish, then Review &amp; snapshot.
                  </div>
                </div>
              </div>
            )}
            <RichReflectionEditor
              value={currentDraft}
              onChange={(html) => setCurrentDraft(html)}
              testidBase={`${testidBase}-current-editor`}
              placeholder="Write the present considered judgment for this movement…"
            />
            {confirmSnapshot ? (
              <div className="mt-3 border-l-2 border-primary pl-3 py-2 bg-primary/5">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-1">Confirmation</div>
                <p className="text-sm leading-relaxed" style={{ fontFamily: "'Merriweather', serif" }}>
                  This will snapshot the current reflection into history as version {historyCount + 1} and replace it with your new text. This is the movement's present considered judgment.
                  {digestGeneratedBy === "yadidai" && digestSources.length > 0 && (
                    <> The promotion will link back to {digestSources.length} sourced Working Journal {digestSources.length === 1 ? "entry" : "entries"}.</>
                  )}
                </p>
                <div className="mt-3 flex gap-2 items-center">
                  <button
                    type="button"
                    onClick={onCommit}
                    disabled={savingCurrent}
                    data-testid={`${testidBase}-current-commit`}
                    className="text-[0.6875rem] font-mono uppercase tracking-widest bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/90 disabled:opacity-60"
                  >
                    {savingCurrent ? "Saving…" : "Snapshot & update"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmSnapshot(false)}
                    className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
                  >
                    Back to edit
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap gap-3 items-center">
                <button
                  type="button"
                  onClick={() => setConfirmSnapshot(true)}
                  disabled={!currentDraft.trim()}
                  data-testid={`${testidBase}-current-review`}
                  className="text-[0.6875rem] font-mono uppercase tracking-widest bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/90 disabled:opacity-40"
                >
                  Review & snapshot
                </button>
                <button
                  type="button"
                  onClick={onCancelEdit}
                  className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
        {!editingCurrent && (
          <div className={`${columnMode ? "mt-8 pt-6 border-t border-border" : "mt-4"} flex flex-wrap items-center gap-4`}>
            <button
              type="button"
              onClick={() => onOpenEditor()}
              data-testid={`${testidBase}-current-update-btn`}
              className={`text-[0.6875rem] font-mono uppercase tracking-widest bg-primary text-primary-foreground rounded-full hover:bg-primary/90 ${columnMode ? "px-6 py-3" : "px-4 py-2"}`}
            >
              Update current reflection
            </button>
            {hasMyGuidedEntries && (
              <button
                type="button"
                onClick={onRunDigest}
                disabled={digestLoading}
                data-testid={`${testidBase}-current-digest-btn`}
                className="text-[0.625rem] font-mono uppercase tracking-widest text-brass border-b border-brass pb-0.5 hover:text-foreground hover:border-foreground disabled:opacity-50"
              >
                {digestLoading ? "Drafting…" : "Summarize what I said →"}
              </button>
            )}
            {historyCount > 0 && (
              <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                § See history · {historyCount} prior version{historyCount === 1 ? "" : "s"}
              </span>
            )}
            {columnMode && (currentUpdatedBy || currentUpdatedAt) && (
              <span className="ml-auto text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
                Version {historyCount + 1}
                {currentUpdatedBy ? ` · ${String(currentUpdatedBy).split("@")[0]}` : ""}
                {currentUpdatedAt ? ` · ${new Date(currentUpdatedAt).toLocaleDateString()}` : ""}
              </span>
            )}
          </div>
        )}

        {/* ── Prior-version history ─────────────────────────────────────
            Read-only. Reverse chronological. Up to 3 versions are laid
            out inline; when more exist, the entire history block wraps
            in a fixed max-height scroll region so the article page
            never grows without bound. The active Current Reflection
            above is intentionally OUTSIDE the scroll region — it must
            never scroll out of view simply because history is deep. */}
        {!editingCurrent && Array.isArray(history) && history.length > 0 && (
          <div
            className={`${columnMode ? "mt-8 pt-6 border-t border-border" : "mt-6 pt-4 border-t border-border"}`}
            data-testid={`${testidBase}-current-history`}
          >
            <div className="flex items-baseline justify-between mb-3 gap-3">
              <div className="text-[0.6875rem] font-mono uppercase tracking-[0.28em] text-muted-foreground">
                § Prior versions
              </div>
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
                {history.length > 3
                  ? `${history.length} total · newest 3 shown, scroll for older`
                  : `${history.length} total · newest first`}
              </div>
            </div>
            <div
              className={`space-y-4 ${history.length > 3 ? "max-h-[26rem] overflow-y-auto overscroll-contain pr-2" : ""}`}
              data-testid={`${testidBase}-current-history-list`}
              style={history.length > 3 ? { WebkitOverflowScrolling: "touch" } : undefined}
            >
              {/*
                Reverse chronological — history[] is stored oldest-first
                by the backend snapshot, so `.slice().reverse()` gives
                us newest first. Each entry's version number is
                (historyCount - index) where index is the display index
                in the reversed list. AI-provenance carried through when
                the snapshot recorded it.
              */}
              {history.slice().reverse().map((h, idx) => {
                const versionNumber = historyCount - idx;
                const author = h.snapshotted_by || h.updated_by || "";
                const when = h.snapshotted_at || h.updated_at || "";
                const body = h.body || h.where_we_are_now || "";
                const aiProv = (h.generated_by || h.where_we_are_now_generated_by) === "yadidai";
                return (
                  <article
                    key={`${when}-${idx}`}
                    className="pl-3 border-l border-border/70"
                    data-testid={`${testidBase}-current-history-entry-${versionNumber}`}
                  >
                    <div className="flex flex-wrap items-center gap-2 text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
                      <span>Version {versionNumber}</span>
                      {author && <><span className="text-border">·</span><span>{String(author).split("@")[0]}</span></>}
                      {when && <><span className="text-border">·</span><span>{new Date(when).toLocaleDateString()}</span></>}
                      {aiProv && (
                        <>
                          <span className="text-border">·</span>
                          <span className="text-brass inline-flex items-center gap-1">
                            <Sparkle size={9} weight="fill" aria-hidden /> AI-drafted · Advisor-approved
                          </span>
                        </>
                      )}
                    </div>
                    <div className="mt-1.5" data-testid={`${testidBase}-current-history-body-${versionNumber}`}>
                      {body ? (
                        <RichReflectionRenderer
                          body={body}
                          columnMode={false}
                          dropCap={false}
                        />
                      ) : (
                        <p className="italic text-muted-foreground/70">(empty — reflection was cleared)</p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
